import { graphql } from 'gatsby';
import { css } from 'styled-components';

import type { BackgroundImageIds } from 'atoms/backgrounds/Background';
import Background from 'atoms/backgrounds/Background';
import { media } from 'atoms/breakpoints/breakpoints';
import type { GetColorDefinition } from 'atoms/colors/colors';
import { margin } from 'atoms/spacing/spacing';
import { font } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Grid from 'quarks/Grid';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';

import Section from 'molecules/Section/Section';

import ComponentButton from 'components/Button/ComponentButton';
import AccordionItem from 'components/CustomerStory/AccordionItem';
import CardDeck from 'components/CustomerStory/CardDeck';
import CustomerDetailHeadingCard from 'components/CustomerStory/HeadingCard';
import TakeawayCard from 'components/CustomerStory/Takeaway';

import Layout from 'layouts/Layout';
import SEO from 'layouts/SEO';

import { getBackgroundColor, toCamelCase } from 'utils/functions';
import richTextParser from 'utils/richTextParser';
import underlineText from 'utils/underlineText';

import type { HeadFC, PageProps } from 'gatsby';
import type { ContentfulTemplateCustomerStory, ContentfulTemplateCustomerStoryConnection } from 'graphqlTypes';
import type { FC } from 'react';

interface CustomerStoryData extends ContentfulTemplateCustomerStory {
  pageData: ContentfulTemplateCustomerStory;
  suggestedStories: ContentfulTemplateCustomerStoryConnection;
}

export const customerStoryColors = {
  'Primary 500': {
    borderColor: 'primary-200',
    background: 'Solid Banner Blue',
  },
  'Red 500': {
    borderColor: 'red-300',
    background: 'Solid Banner Red',
  },
  'Purple 500': {
    borderColor: 'purple-100',
    background: 'Solid Banner Purple',
  },
  'Green 500': {
    borderColor: 'green-100',
    background: 'Solid Banner Green',
  },
  'Yellow 500': {
    borderColor: 'yellow-200',
    background: 'Solid Banner Yellow',
  },
};

export type customerStoryColorIds = keyof typeof customerStoryColors;

const bodyStyles = css`
  h2 {
    font: ${font.display.sm.font()};
    letter-spacing: ${font.display['sm'].letterSpacing};
    text-align: center;
    margin-bottom: ${margin[32]};
    padding-bottom: ${margin[24]};
  }
  p {
    margin-bottom: ${margin[32]};
    font: ${font.text.md.font()};
  }
  .blockquote {
    margin-bottom: ${margin[32]};
  }
  .blockquote p {
    margin-bottom: 0;
    font: ${font.text.xl.font()};
  }

  ${media.xl} {
    h2 {
      font: ${font.display.lg.font()};
      letter-spacing: ${font.display.lg.letterSpacing};
      padding-bottom: ${margin[32]};
    }
    p {
      font: ${font.text.xl.font()};
    }
    .blockquote p {
      font: ${font.display.xs.font()};
    }
  }
`;

const CustomerStory: FC<PageProps<CustomerStoryData>> = ({
  data: {
    pageData: {
      slug,
      backgroundColor,
      company,
      heading,
      subheading,
      growingWithUs,
      companySize,
      industry,
      companyLocation,
      takeawaysCardDeck,
      howItBegan,
      accordion,
      content,
    },
    suggestedStories,
  },
  location,
}) => {
  if (!company) {
    throw new Error(`Page at ${location.pathname} is missing Company`);
  }

  const backgroundImage = toCamelCase(
    customerStoryColors[backgroundColor as customerStoryColorIds].background,
  ) as BackgroundImageIds;
  const borderColor = customerStoryColors[backgroundColor as customerStoryColorIds].borderColor as GetColorDefinition;

  const headingBackground = (
    <>
      <Background backgroundImageId={backgroundImage} display="none" xl={{ display: 'block' }} maxHeight="75%" />
      <Container
        position="absolute"
        zIndex={-10}
        maxHeight="25%"
        width="100%"
        right={0}
        left={0}
        top={0}
        bottom={0}
        backgroundColor={getBackgroundColor(backgroundColor || 'White') as GetColorDefinition}
        display="block"
        xl={{ display: 'none' }}
      />
    </>
  );

  const pagePath = slug && (slug[0] === '/' ? slug : `/${slug}`);

  const headingComponent = heading && subheading?.subheading && (
    <Flex alignItems="center">
      <Flex
        flexDirection="column"
        textAlign="center"
        alignItems="center"
        gap="24px"
        width="100%"
        marginX="auto"
        textColor="gray-900"
        xl={{
          textAlign: 'left',
          alignItems: 'flex-start',
        }}
      >
        <Flex flexDirection="column" gap="16px">
          <Text textStyle="eyebrowLg" fontWeight="bold">
            {company.name}
          </Text>

          <Heading as="h1" fontWeight="extraBold" textStyle="sm" xl={{ textStyle: 'lg' }}>
            {underlineText(heading)}
          </Heading>
          <Text textStyle="lg" xl={{ textStyle: 'xl' }}>
            {subheading.subheading}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );

  const takeaways = takeawaysCardDeck && (
    <Flex
      borderRadius="6px"
      backgroundColor="common-white"
      flexDirection="column"
      borderColor={borderColor}
      marginY={48}
      xl={{
        borderTop: backgroundColor ? '16px solid' : 'none',
        borderColor,
        paddingX: 48,
        paddingY: 32,
      }}
      alignItems="center"
      zIndex={10}
    >
      <Heading as="h2" textStyle="xs" marginBottom={48}>
        Takeaways
      </Heading>
      <Flex
        flexDirection="column"
        width="100%"
        justifyContent="center"
        alignItems="center"
        gap="24px"
        xl={{
          flexDirection: 'row',
          gap: '32px',
        }}
      >
        {takeawaysCardDeck.cards?.map(
          (card, index) =>
            card && (
              <TakeawayCard
                card={card.__typename === 'ContentfulComponentBasicCard' ? card : undefined}
                href={`/our-customers${pagePath}#section-${index + 1}`}
                // eslint-disable-next-line react/no-array-index-key
                key={`${card.id}-${index}`}
              />
            ),
        )}
      </Flex>
    </Flex>
  );

  const headingSection = heading && (
    <Section
      id={`${company.name}-heading`}
      paddingTopMobile={32}
      paddingTopTablet={32}
      paddingTopDesktop={32}
      paddingBottomMobile={32}
      paddingBottomTablet={32}
      paddingBottomDesktop={32}
    >
      <Flex flexDirection="column" md={{ marginX: 16 }} lg={{ marginX: 64 }} xl={{ marginY: 16, marginX: 0 }}>
        {headingBackground}
        <Grid
          gridTemplateColumns="1fr"
          gap="32px"
          xl={{
            gridTemplateColumns: 'repeat(2, 1fr)',
            marginBottom: 64,
          }}
          zIndex={10}
        >
          {headingComponent}
          <CustomerDetailHeadingCard
            company={company}
            growingWithUs={growingWithUs}
            companyLocation={companyLocation}
            companySize={companySize}
            industry={industry}
            borderColor={borderColor}
          />
        </Grid>
        {takeaways}
      </Flex>
    </Section>
  );

  const howItBeganSection = howItBegan && (
    <Section
      id={`${company.name}-how-it-began`}
      paddingTopMobile={32}
      paddingTopTablet={32}
      paddingTopDesktop={32}
      paddingBottomMobile={32}
      paddingBottomTablet={32}
      paddingBottomDesktop={32}
    >
      <Flex id="section-1" justifyContent="center" xl={{ marginY: 16 }}>
        <Flex
          width="100%"
          flexDirection="column"
          lg={{
            maxWidth: '736px',
          }}
          css={bodyStyles}
        >
          <Heading as="h2" textAlign="center" textStyle="sm" xl={{ textStyle: 'lg' }}>
            {underlineText('{%How It Began%}')}
          </Heading>
          {richTextParser(howItBegan)}
        </Flex>
      </Flex>
    </Section>
  );

  const accordionSection = accordion && (
    <Section
      id={`${company.name}-accordion`}
      backgroundColor="gray-100"
      paddingTopMobile={32}
      paddingTopTablet={32}
      paddingTopDesktop={32}
      paddingBottomMobile={32}
      paddingBottomTablet={32}
      paddingBottomDesktop={32}
    >
      <Flex id="section-2" alignItems="center" flexDirection="column" marginY={16} xl={{ marginY: 32 }}>
        <Heading
          as="h2"
          textAlign="center"
          textStyle="sm"
          marginBottom={48}
          xl={{
            textStyle: 'lg',
          }}
        >
          {underlineText(`Why ${company.name} {%Chooses Justworks%}`)}
        </Heading>
        <Flex
          width="100%"
          maxWidth="800px"
          md={{
            paddingX: 16,
          }}
          flexDirection="column"
          alignItems="center"
          gap="16px"
        >
          {accordion.accordionItems &&
            accordion.accordionItems.map(
              (accordionItem, index) =>
                accordionItem && (
                  <AccordionItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${accordionItem.id}-${index}`}
                    {...accordionItem}
                    index={index}
                  />
                ),
            )}
        </Flex>
      </Flex>
    </Section>
  );

  const contentSection = content && (
    <Section
      id={`${company.name}-content`}
      paddingTopMobile={64}
      paddingTopTablet={64}
      paddingTopDesktop={64}
      paddingBottomMobile={32}
      paddingBottomTablet={32}
      paddingBottomDesktop={32}
    >
      <Flex id="section-3" justifyContent="center" xl={{ marginTop: 32, marginBottom: 16 }}>
        <Flex
          width="100%"
          flexDirection="column"
          lg={{
            maxWidth: '736px',
          }}
          xl={{
            marginY: 16,
          }}
          css={bodyStyles}
        >
          {richTextParser(content)}
        </Flex>
      </Flex>
    </Section>
  );

  const moreCustomersSection = suggestedStories.nodes && (
    <Section
      id={`${company.name}-more-customers`}
      paddingTopMobile={32}
      paddingTopTablet={32}
      paddingTopDesktop={32}
      paddingBottomMobile={32}
      paddingBottomTablet={32}
      paddingBottomDesktop={32}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        xl={{
          marginY: 16,
        }}
      >
        <Heading
          as="h2"
          textAlign="center"
          textStyle="sm"
          marginBottom={32}
          xl={{
            textStyle: 'lg',
          }}
        >
          {underlineText('More from {%customers like you%}')}
        </Heading>
        <CardDeck cards={suggestedStories.nodes} />
      </Flex>
    </Section>
  );

  const getStartedSection = (
    <Section
      id="get-started"
      backgroundColor="primary-900"
      paddingTopMobile={64}
      paddingTopTablet={64}
      paddingTopDesktop={64}
      paddingBottomMobile={64}
      paddingBottomTablet={64}
      paddingBottomDesktop={64}
    >
      <Flex
        alignItems="center"
        textAlign="center"
        textColor="common-white"
        flexDirection="column"
        xl={{
          marginY: 32,
        }}
      >
        <Heading
          as="h2"
          textStyle="sm"
          xl={{
            textStyle: 'lg',
          }}
        >
          Get Started With Justworks.
        </Heading>
        <Text
          fontSize="textMd"
          xl={{
            fontSize: 'textLg',
          }}
          marginBottom={24}
        >
          Together, we&#39;ll build a program that works for your business.
        </Text>
        <Flex>
          <ComponentButton
            hierarchy="Primary Color"
            label="Get Started"
            href="/guided-shopping/basic/how-many-employees?cta=case-study"
          />
        </Flex>
      </Flex>
    </Section>
  );

  return (
    <Layout>
      {headingSection}
      {howItBeganSection}
      {accordionSection}
      {contentSection}
      {moreCustomersSection}
      {getStartedSection}
    </Layout>
  );
};

export default CustomerStory;

export const Head: HeadFC<CustomerStoryData> = ({
  data: {
    pageData: { seo },
  },
  location: { pathname },
}) => <SEO pathname={pathname} {...seo} />;

export const customerStoryQuery = graphql`
  query templateCustomerStoryQuery($slug: String!, $solutionsArray: [String]) {
    pageData: contentfulTemplateCustomerStory(slug: { eq: $slug }) {
      ...contentfulTemplateCustomerStory
    }
    suggestedStories: allContentfulTemplateCustomerStory(
      filter: { solutionsOffered: { in: $solutionsArray }, slug: { ne: $slug, glob: "!**/*dummy*" } }
      limit: 3
    ) {
      nodes {
        ...contentfulTemplateCustomerStory
      }
    }
  }
`;
